import {Flex} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {isFlowchartType} from 'processes/processes-platform/FeatureOptions';
import {
  MinimalProcessTaskSchemaEditorQuery,
  MinimalProcessTaskSchemaEditorQueryVariables,
} from 'processes/settings/__generated/MinimalProcessTaskSchemaEditorQuery';
import {
  MINIMAL_PROCESS_TASK_SCHEMA_EDITOR_QUERY,
  ProcessTaskSchemaEditorContent,
  ProcessTaskSchemaEditorContentSections,
  ProcessTaskSchemaEditorTabbedContainer,
} from 'processes/settings/ProcessTaskSchemaEditor';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';

import {ProcessFlowchartMetadataEditorProps} from '../ProcessFlowchartConfigEditorLeftSidepanel';
import {FlowchartMetadataOptionsChangeType} from '../processFlowchartConfigEditorUtils';
import {useAllSchemasById} from '../useAllSchemasById';

const styles = StyleSheet.create({
  checkboxWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkboxSection: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    marginTop: '12px',
    marginBottom: '12px',
    paddingTop: '12px',
  },
  checkbox: {
    marginTop: '12px',
    marginBottom: '12px',
  },
});

function FlowchartSchemaConfigurationTab(props: ProcessFlowchartMetadataEditorProps): JSX.Element {
  const {
    processTaskSchemas: allTaskSchemas,
    storableSchemas,
    tagSchemas,
    resultSchemas,
    statusLifecycles,
    haveSchemasLoaded,
  } = useAllSchemasById(props.flowchartTaskSchema);
  const locked = ReactFlowLockedContext.useContext() ?? false;

  const handleToggleIsFlowchartModifiableOnCreation = (isFlowchartModifiableOnCreation: boolean): void => {
    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.UPDATED_FLOWCHART_SETTINGS, {
      actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
      settingName: 'isFlowchartModifiableOnCreation',
      settingValue: isFlowchartModifiableOnCreation,
      flowchartType: props.flowchartTypeName,
    });
    props.applyFlowchartChanges(() => ({
      nodeChanges: [],
      edgeChanges: [],
      transitionSourceChanges: [],
      customMappingChanges: [],
      metadataOptionsChanges: [
        {type: FlowchartMetadataOptionsChangeType.UPDATE, options: {isFlowchartModifiableOnCreation}},
      ],
    }));
  };

  const handleToggleIsFlowchartModifiableAfterCreation = (
    isFlowchartModifiableAfterCreation: boolean
  ): void => {
    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.UPDATED_FLOWCHART_SETTINGS, {
      actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
      settingName: 'isFlowchartModifiableAfterCreation',
      settingValue: isFlowchartModifiableAfterCreation,
      flowchartType: props.flowchartTypeName,
    });
    props.applyFlowchartChanges(() => ({
      nodeChanges: [],
      edgeChanges: [],
      transitionSourceChanges: [],
      customMappingChanges: [],
      metadataOptionsChanges: [
        {type: FlowchartMetadataOptionsChangeType.UPDATE, options: {isFlowchartModifiableAfterCreation}},
      ],
    }));
  };

  const areFlowchartModificationOptionsEnabled =
    BenchlingConfig.WORKFLOWS_ENABLE_ACM_PCM_TRANSITIONS_IN_HORIZONTAL_FLOWCHART && !locked;

  return !haveSchemasLoaded ? (
    <CenteredHorizontalThrobber />
  ) : (
    <div>
      <ProcessTaskSchemaEditorContent
        noSectionSpacing={true}
        processTaskSchema={{...props.flowchartTaskSchema, flowchartConfig: null, relevantForms: []}}
        storableSchemas={storableSchemas}
        tagSchemas={tagSchemas}
        taskSchemas={
          // We do not need flowchart type task schemas here because they are not needed for basic task configuration
          allTaskSchemas
            ?.filter((schema) => !isFlowchartType(schema.executionType))
            .map((schema) => ({...schema, flowchartConfig: null})) ?? []
        }
        statusLifecycles={statusLifecycles}
        resultSchemas={resultSchemas}
        isAnyPartOfFlowchartConfigInaccessible={null}
        sectionsToHide={[
          ProcessTaskSchemaEditorContentSections.TASK_FIELDS,
          ProcessTaskSchemaEditorContentSections.OUTPUT,
          ProcessTaskSchemaEditorContentSections.FLOWCHART,
        ]}
        locked={locked}
      />
      <div className={css(styles.checkboxSection)}>
        <div className={css(styles.checkboxWrapper)}>
          <CheckboxInput
            id="isFlowchartModifiableOnCreation"
            className={css(styles.checkbox)}
            checked={props.flowchartConfigMetadata.options.isFlowchartModifiableOnCreation}
            label="Users can modify the flowchart when creating tasks"
            onChange={(e) => {
              handleToggleIsFlowchartModifiableOnCreation(e.target.checked);
            }}
            disabled={!areFlowchartModificationOptionsEnabled}
            dataTestAttrs={{
              component: 'FlowchartSchemaConfigurationTab',
              element: 'isFlowchartModifiableOnCreationCheckbox',
            }}
          />
        </div>
        <div className={css(styles.checkboxWrapper)}>
          <CheckboxInput
            id="isFlowchartModifiableAfterCreation"
            className={css(styles.checkbox)}
            checked={props.flowchartConfigMetadata.options.isFlowchartModifiableAfterCreation}
            label="Users can modify the flowchart after tasks have been created"
            onChange={(e) => {
              handleToggleIsFlowchartModifiableAfterCreation(e.target.checked);
            }}
            disabled={!areFlowchartModificationOptionsEnabled}
            dataTestAttrs={{
              component: 'FlowchartSchemaConfigurationTab',
              element: 'isFlowchartModifiableAfterCreationCheckbox',
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function FlowchartSettingsSidepanelContent(props: ProcessFlowchartMetadataEditorProps): JSX.Element {
  return (
    <Flex pt="md" px="md">
      {/* The ProcessTaskSchemaEditor expects to also see forms and request templates. Query them here */}
      <BenchlingQuery<MinimalProcessTaskSchemaEditorQuery, MinimalProcessTaskSchemaEditorQueryVariables>
        query={MINIMAL_PROCESS_TASK_SCHEMA_EDITOR_QUERY}
        renderLoading={() => <CenteredHorizontalThrobber />}
        variables={{schemaId: props.flowchartTaskSchema.id}}
        fetchPolicy="cache-and-network"
      >
        {(data) =>
          (() => {
            if (isInaccessible(data.processTaskSchema)) {
              return (
                <WorkspaceItemInaccessibleGuard
                  inaccessibleInfo={data.processTaskSchema}
                  modelType="processTaskSchema"
                />
              );
            }
            return (
              <ProcessTaskSchemaEditorTabbedContainer
                subtitle={props.flowchartTaskSchema.name}
                processTaskSchema={data.processTaskSchema}
                configurationTab={
                  <FlowchartSchemaConfigurationTab
                    flowchartTaskSchema={data.processTaskSchema}
                    flowchartConfigMetadata={props.flowchartConfigMetadata}
                    applyFlowchartChanges={props.applyFlowchartChanges}
                    flowchartTypeName={props.flowchartTypeName}
                  />
                }
                schemaId={props.flowchartTaskSchema.id}
              />
            );
          })()
        }
      </BenchlingQuery>
    </Flex>
  );
}
